import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { BlAmendFormModalService, BlAmendFormData } from 'src/app/services/bl-amend-form-modal/bl-amend-form-modal.service';
import { VehicleBlFormService } from 'src/app/services/bl-form/vehicle-bl-form.service';

declare var bootstrap: any;

@Component({
  selector: 'app-bl-amend-form',
  templateUrl: './bl-amend-form.component.html',
  styleUrls: ['./bl-amend-form.component.scss']
})
export class BlAmendFormComponent implements OnInit, OnDestroy {

  selectedData: BlAmendFormData | null = null;
  order: any = null;
  formStage: number = 1;

  blForm1: FormGroup;
  blForm2: FormGroup;
  blForm3: FormGroup;

  isLoading1: boolean = false;
  isLoading2: boolean = false;
  isLoading3: boolean = false;

  existingBlFormData: any = null;

  // Section checkboxes for each form (vehicle checkboxes are dynamically added)
  sectionCheckboxes: { [formNumber: number]: { [section: string]: boolean } } = {
    1: { shipper_information: false, consignee_information: false, notify_party_information: false, shipping_details: false },
    2: { shipper_information: false, consignee_information: false, notify_party_information: false, shipping_details: false },
    3: { shipper_information: false, consignee_information: false, notify_party_information: false, shipping_details: false }
  };

  private subscriptions: Subscription[] = [];

  // Non-vehicle form field keys (shipper, consignee, notify party, shipping)
  shipperFields = [
    { key: 'shipper_company', label: 'Shipper Company' },
    { key: 'shipper_address', label: 'Address' },
    { key: 'shipper_city', label: 'City' },
    { key: 'shipper_country', label: 'Country' },
    { key: 'shipper_zip', label: 'Zip' },
    { key: 'shipper_tel', label: 'Tel No.' }
  ];

  consigneeFields = [
    { key: 'consignee_company', label: 'Consignee Company' },
    { key: 'consignee_name', label: 'Consignee Name' },
    { key: 'consignee_address', label: 'Address' },
    { key: 'consignee_city', label: 'City' },
    { key: 'consignee_zip', label: 'Zip' },
    { key: 'consignee_country', label: 'Country' },
    { key: 'consignee_tel', label: 'Tel No.' },
    { key: 'consignee_fax', label: 'Fax' },
    { key: 'consignee_email', label: 'Email Id' }
  ];

  notifyPartyFields = [
    { key: 'notify_party_name', label: 'Customer/Notify Party' },
    { key: 'notify_party_address', label: 'Address 1' },
    { key: 'notify_party_city', label: 'City' },
    { key: 'notify_party_zip', label: 'ZIP' },
    { key: 'notify_party_country', label: 'Country' },
    { key: 'notify_party_tel', label: 'Tel No.' },
    { key: 'notify_party_fax', label: 'Fax' },
    { key: 'notify_party_email', label: 'Email Id' }
  ];

  // Vehicle-specific fields (will be repeated for each vehicle)
  vehicleDetailFields = [
    { key: 'make', label: 'Make' },
    { key: 'model', label: 'Model' },
    { key: 'model_code', label: 'Model Code' },
    { key: 'fuel', label: 'Fuel' },
    { key: 'hs_code', label: 'HS CODE' },
    { key: 'lwh', label: 'LWH' },
    { key: 'engine_no', label: 'Engine No.' },
    { key: 'mileage', label: 'Mileage' },
    { key: 'engine_cc', label: 'Engine CC' }
  ];

  shippingFields = [
    { key: 'ship_name', label: 'Ship Name' },
    { key: 'carrier_name', label: 'Carrier Name' },
    { key: 'etd_date', label: 'ETD/Sailing Date' },
    { key: 'eta_date', label: 'ETA/Arrival Date' },
    { key: 'sailing_port', label: 'Sailing Port' },
    { key: 'destination_port', label: 'Destination Port' },
    { key: 'destination_country', label: 'Destination Country' },
    { key: 'one_unit', label: 'One Unit' },
    { key: 'used_vehicle', label: 'Used Vehicle' },
    { key: 'other_options', label: 'Other Options' },
    { key: 'remarks', label: 'Remarks' }
  ];

  // Combined form fields for backward compatibility
  formFields: { key: string; label: string }[] = [];

  constructor(
    private _fb: FormBuilder,
    private _blAmendFormModalService: BlAmendFormModalService,
    private _vehicleBlFormService: VehicleBlFormService
  ) {
    // Initialize empty forms - will be rebuilt when order data is available
    this.blForm1 = this._fb.group({});
    this.blForm2 = this._fb.group({});
    this.blForm3 = this._fb.group({});
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this._blAmendFormModalService.selectedData$.subscribe(data => {
        if (data) {
          this.selectedData = data;
          this.order = data.order;
          this.formStage = this.order.bl_form_stage || 1;
          this.buildFormFields();
          this.rebuildForms();
          this.updateFormsDisabledState();
          this.loadExistingData();
        }
      })
    );
    this.setupModalListener();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Build formFields array dynamically based on vehicles count
  private buildFormFields(): void {
    this.formFields = [
      ...this.shipperFields,
      ...this.consigneeFields,
      ...this.notifyPartyFields
    ];

    // Add vehicle detail fields for each vehicle
    const vehicleCount = this.order?.vehicles?.length || 1;
    for (let i = 0; i < vehicleCount; i++) {
      this.vehicleDetailFields.forEach(field => {
        this.formFields.push({
          key: `vehicle_${i}_${field.key}`,
          label: field.label
        });
      });
    }

    this.formFields.push(...this.shippingFields);

    // Initialize vehicle include checkboxes for each form
    this.initializeVehicleCheckboxes(vehicleCount);
  }

  // Initialize dynamic vehicle checkboxes
  private initializeVehicleCheckboxes(vehicleCount: number): void {
    for (let formNum = 1; formNum <= 3; formNum++) {
      for (let i = 0; i < vehicleCount; i++) {
        const key = `vehicle_${i}_include`;
        if (this.sectionCheckboxes[formNum][key] === undefined) {
          this.sectionCheckboxes[formNum][key] = false;
        }
      }
    }
  }

  // Rebuild forms with current formFields
  private rebuildForms(): void {
    this.blForm1 = this.createFormGroup(1);
    this.blForm2 = this.createFormGroup(2);
    this.blForm3 = this.createFormGroup(3);
  }

  private createFormGroup(form_number: number): FormGroup {
    const formControls: any = {};

    this.formFields.forEach(field => {
      formControls[field.key] = [
        { value: '', disabled: this.isFormDisabled(form_number) }
      ];
    });

    return this._fb.group(formControls);
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_bl_amend_form');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceData = this._blAmendFormModalService.getSelectedData();
        if (serviceData) {
          this.selectedData = serviceData;
          this.order = serviceData.order;
          this.formStage = this.order.bl_form_stage || 1;
          this.buildFormFields();
          this.rebuildForms();
          this.updateFormsDisabledState();
          this.loadExistingData();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._blAmendFormModalService.clearSelectedData();
        this.resetForms();
      });
    }
  }

  private loadExistingData(): void {
    if (!this.order?._id) return;

    this.subscriptions.push(
      this._vehicleBlFormService.GetData("1", '', {order_id:this.order._id,paginate:false}).subscribe({
        next: (response: any) => {
          if (response.status && response.data && response.data.length > 0) {
            this.existingBlFormData = response.data[0];
            this.populateForms();
          }
        },
        error: (error: any) => {
          console.error('Error loading BL form data:', error);
        }
      })
    );
  }

  private populateForms(): void {
    if (!this.existingBlFormData) return;

    if (this.existingBlFormData.bl_amendment_1) {
      this.blForm1.patchValue(this.existingBlFormData.bl_amendment_1);
      this.populateSectionCheckboxes(1, this.existingBlFormData.bl_amendment_1);
    }
    if (this.existingBlFormData.bl_amendment_2) {
      this.blForm2.patchValue(this.existingBlFormData.bl_amendment_2);
      this.populateSectionCheckboxes(2, this.existingBlFormData.bl_amendment_2);
    }
    if (this.existingBlFormData.bl_amendment_3) {
      this.blForm3.patchValue(this.existingBlFormData.bl_amendment_3);
      this.populateSectionCheckboxes(3, this.existingBlFormData.bl_amendment_3);
    }
  }

  private populateSectionCheckboxes(formNumber: number, data: any): void {
    // Static section keys
    const sectionKeys = ['shipper_information', 'consignee_information', 'notify_party_information', 'shipping_details'];
    sectionKeys.forEach(key => {
      if (data[key] !== undefined) {
        this.sectionCheckboxes[formNumber][key] = data[key];
      }
    });

    // Dynamic vehicle include checkboxes
    const vehicleCount = this.order?.vehicles?.length || 1;
    for (let i = 0; i < vehicleCount; i++) {
      const key = `vehicle_${i}_include`;
      if (data[key] !== undefined) {
        this.sectionCheckboxes[formNumber][key] = data[key];
      }
    }
  }

  private resetForms(): void {
    this.blForm1.reset();
    this.blForm2.reset();
    this.blForm3.reset();
    this.existingBlFormData = null;
    this.order = null;
    this.selectedData = null;
    this.resetSectionCheckboxes();
  }

  private resetSectionCheckboxes(): void {
    for (let i = 1; i <= 3; i++) {
      this.sectionCheckboxes[i] = {
        shipper_information: false,
        consignee_information: false,
        notify_party_information: false,
        shipping_details: false
      };
    }
  }

  getOriginalValue(key: string): string {
    if (!this.order) return '';

    const shipper = this.order.shipper_details || {};

    // Check if this is a vehicle-indexed key (e.g., vehicle_0_make, vehicle_1_model)
    const vehicleKeyMatch = key.match(/^vehicle_(\d+)_(.+)$/);
    if (vehicleKeyMatch) {
      const vehicleIndex = parseInt(vehicleKeyMatch[1], 10);
      const fieldKey = vehicleKeyMatch[2];
      const vehicle = this.order.vehicles?.[vehicleIndex] || {};
      return this.getVehicleFieldValue(vehicle, fieldKey);
    }

    // Non-vehicle field mappings
    const mappings: { [key: string]: string } = {
      shipper_company: shipper.company_name || '',
      shipper_address: shipper.address_1 || '',
      shipper_city: shipper.city || '',
      shipper_country: shipper.country || '',
      shipper_zip: shipper.zip || '',
      shipper_tel: shipper.telephone_number || '',
      consignee_company: this.order.buyer_company_name || '',
      consignee_name: this.order.buyer_consignee_name || '',
      consignee_address: this.order.buyer_street_address || '',
      consignee_city: this.order.buyer_city || '',
      consignee_zip: this.order.buyer_zip || '',
      consignee_country: this.order.buyer_country || '',
      consignee_tel: this.order.buyer_telephone_no || '',
      consignee_fax: this.order.buyer_fax || '',
      consignee_email: this.order.buyer_email_address || '',
      notify_party_name: this.order.same_as_consignee ? this.order.buyer_consignee_name : this.order.notify_party_name || '',
      notify_party_address: this.order.same_as_consignee ? this.order.buyer_street_address : this.order.notify_party_address || '',
      notify_party_city: this.order.same_as_consignee ? this.order.buyer_city : this.order.notify_party_city || '',
      notify_party_zip: this.order.same_as_consignee ? this.order.buyer_zip : this.order.notify_party_zip || '',
      notify_party_country: this.order.same_as_consignee ? this.order.buyer_country : this.order.notify_party_country || '',
      notify_party_tel: this.order.same_as_consignee ? this.order.buyer_telephone_no : this.order.notify_party_tel || '',
      notify_party_fax: this.order.same_as_consignee ? this.order.buyer_fax : this.order.notify_party_fax || '',
      notify_party_email: this.order.same_as_consignee ? this.order.buyer_email_address : this.order.notify_party_email || '',
      ship_name: this.order.ship_name || '',
      carrier_name: this.order.carrier_name || '',
      etd_date: this.order.etd_date || '',
      eta_date: this.order.eta_date || '',
      sailing_port: this.order.from_port || '',
      destination_port: this.order.to_port || '',
      destination_country: this.order.to_country || '',
      one_unit: this.order.vehicles?.[0]?.one_unit || '',
      used_vehicle: this.order.vehicles?.[0]?.used_vehicle || 'Yes',
      other_options: this.order.vehicles?.[0]?.other_options || '',
      remarks: this.order.remarks || ''
    };

    return mappings[key] || '';
  }

  private getVehicleFieldValue(vehicle: any, fieldKey: string): string {
    const vehicleMappings: { [key: string]: string } = {
      make: vehicle.company_name || '',
      model: vehicle.name || '',
      model_code: vehicle?.model_code_obj?.code || '',
      fuel: vehicle.fuel_type || '',
      hs_code: vehicle.hs_code || '',
      lwh: `${vehicle.car_length || ''}x${vehicle.car_width || ''}x${vehicle.car_height || ''}`,
      engine_no: vehicle.engine_no || '',
      mileage: vehicle.mileage || '',
      engine_cc: vehicle.engine || vehicle.displacement || ''
    };
    return vehicleMappings[fieldKey] || '';
  }

  copyToAmendment(sourceIndex: number): void {
    // sourceIndex: 0 = Original Details, 1 = BL Amendment 1, 2 = BL Amendment 2
    let sourceData: { [key: string]: string } = {};

    if (sourceIndex === 0) {
      // Copy from Original Details
      this.formFields.forEach(field => {
        sourceData[field.key] = this.getOriginalValue(field.key);
      });
      this.blForm1.patchValue(sourceData);
    } else if (sourceIndex === 1) {
      // Copy from BL Amendment 1 to BL Amendment 2
      this.blForm2.patchValue(this.blForm1.value);
    } else if (sourceIndex === 2) {
      // Copy from BL Amendment 2 to BL Amendment 3
      this.blForm3.patchValue(this.blForm2.value);
    }
  }

  saveAmendment(amendmentNumber: number): void {
    const formData = amendmentNumber === 1 ? this.blForm1.getRawValue() :
      amendmentNumber === 2 ? this.blForm2.getRawValue() : this.blForm3.getRawValue();

    // Include section checkboxes in form data
    const formDataWithSections = {
      ...formData,
      ...this.sectionCheckboxes[amendmentNumber]
    };

    const payload: any = {
      order_id: this.order._id,
      [`bl_amendment_${amendmentNumber}`]: formDataWithSections
    };

    if (amendmentNumber === 1) this.isLoading1 = true;
    else if (amendmentNumber === 2) this.isLoading2 = true;
    else this.isLoading3 = true;

    const apiCall = this.existingBlFormData?._id
      ? this._vehicleBlFormService.UpdateData(this.existingBlFormData._id, payload)
      : this._vehicleBlFormService.AddData(payload);

    this.subscriptions.push(
      apiCall.subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.existingBlFormData = response.data;
          }
          this.isLoading1 = this.isLoading2 = this.isLoading3 = false;
        },
        error: (error: any) => {
          console.error('Error saving BL amendment:', error);
          this.isLoading1 = this.isLoading2 = this.isLoading3 = false;
        }
      })
    );
  }

  private dummyControl = new FormControl({ value: '', disabled: true });

  getFormControl(form: FormGroup, key: string): FormControl {
    const control = form.get(key);
    return control ? (control as FormControl) : this.dummyControl;
  }

  // Check if a form should be disabled based on formStage
  isFormDisabled(formNumber: number): boolean {
    return formNumber !== this.formStage;
  }

  // Update all forms' disabled state based on current formStage
  private updateFormsDisabledState(): void {
    this.updateFormDisabledState(this.blForm1, 1);
    this.updateFormDisabledState(this.blForm2, 2);
    this.updateFormDisabledState(this.blForm3, 3);
  }

  private updateFormDisabledState(form: FormGroup, formNumber: number): void {
    const shouldDisable = this.isFormDisabled(formNumber);
    this.formFields.forEach(field => {
      const control = form.get(field.key);
      if (control) {
        if (shouldDisable) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });
  }
}
